/**
 * @file: ChatThreadListView.tsx
 *
 * Chat thread list — the "聊天消息" surface. Navigation-agnostic embeddable
 * content (the inbox tab / desktop master column / the screen wrapper mount
 * it): the host passes `onOpenThread` (called with the raw thread) and an
 * optional `isActive` predicate for the desktop master-detail highlight. Data
 * + sort + pull-to-refresh live in `useThreadList`; rows in `ChatThreadRow`.
 */

import { ChatRefusalNotice } from '@euler/app/flows/chat/components/ChatRefusalNotice';
import { EmptyMessagesIcon } from '@euler/app/flows/chat/components/EmptyMessagesIcon';
import { useChatRefusal } from '@euler/app/flows/chat/lib/session/hooks/useChatRefusal';
import { usePresenceRefresh } from '@euler/app/flows/chat/lib/session/hooks/usePresence';
import { chatThreadIsPinned } from '@euler/app/flows/chat/utils/chatThreadDisplay';
import { useInboxListTheme } from '@euler/app/flows/inbox/lib/listTheme';
import { ListEmptyState } from '@euler/components/ListEmptyState';
import { useAlert } from '@euler/functions/ux/useAlert';
import { ChatThreadItem } from '@euler/model/chat';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FC, memo, useCallback } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  View,
} from 'react-native';
import { useThreadList } from '../functions/useThreadList';
import { SwipeableThreadRow } from './SwipeableThreadRow';
import { ThreadListSkeleton } from './ThreadListSkeleton';

export const ChatThreadListView: FC<{
  /** Highlight predicate for the desktop master-detail column. */
  isActive?: (thread: ChatThreadItem) => boolean;
  onOpenThread: (thread: ChatThreadItem) => void;
}> = memo(({ isActive, onOpenThread }) => {
  const theme = useInboxListTheme();
  const {
    sortedThreads,
    loading,
    refreshing,
    onRefresh,
    setThreadFlag,
    deleteThread,
  } = useThreadList();
  // Seed online dots for the visible private peers (live `presence` events
  // keep them current after this snapshot).
  usePresenceRefresh(
    sortedThreads
      .map(t => t.peer)
      .filter((p): p is NonNullable<ChatThreadItem['peer']> => p != null),
  );
  const alert = useAlert();
  const { showActionSheetWithOptions } = useActionSheet();

  const onTogglePin = useCallback(
    (thread: ChatThreadItem) => {
      setThreadFlag(thread.id, 'pinned', !chatThreadIsPinned(thread));
    },
    [setThreadFlag],
  );

  const onToggleMute = useCallback(
    (thread: ChatThreadItem) => {
      setThreadFlag(thread.id, 'muted', thread.mutedAt == null);
    },
    [setThreadFlag],
  );

  const onDelete = useCallback(
    (thread: ChatThreadItem) => {
      const remove = () => deleteThread(thread.id);
      const message = '将从列表移除并清空本地聊天记录，新消息会重新创建会话。';
      // Native: the iOS-style action sheet (destructive 删除 + 取消). Web has
      // no native sheet metaphor, so keep the confirm dialog there.
      if (Platform.OS === 'web') {
        void alert({
          title: '删除会话',
          message: `${message}确定删除吗？`,
          buttons: [
            { text: '取消', style: 'cancel' },
            {
              text: '删除',
              style: 'destructive',
              onPress: remove,
            },
          ],
        });
        return;
      }
      showActionSheetWithOptions(
        {
          title: '删除会话',
          message,
          options: ['删除', '取消'],
          destructiveButtonIndex: 0,
          cancelButtonIndex: 1,
        },
        i => {
          if (i === 0) remove();
        },
      );
    },
    [alert, showActionSheetWithOptions, deleteThread],
  );

  const renderItem: ListRenderItem<ChatThreadItem> = useCallback(
    ({ item, index }) => {
      // The last pinned row carries the edge-to-edge section divider (the
      // boundary between pinned and the rest), since the list is pinned-first.
      const next = sortedThreads[index + 1];
      const isLastPinned =
        chatThreadIsPinned(item) && (next == null || !chatThreadIsPinned(next));
      return (
        <SwipeableThreadRow
          thread={item}
          active={isActive?.(item) ?? false}
          fullWidthDivider={isLastPinned}
          onPress={() => onOpenThread(item)}
          onTogglePin={onTogglePin}
          onToggleMute={onToggleMute}
          onDelete={onDelete}
        />
      );
    },
    [
      sortedThreads,
      isActive,
      onOpenThread,
      onTogglePin,
      onToggleMute,
      onDelete,
    ],
  );

  const keyExtractor = useCallback((item: ChatThreadItem) => item.id, []);
  const refusal = useChatRefusal();

  if (refusal != null) return <ChatRefusalNotice refusal={refusal} />;

  return (
    // Panel bg comes from the inbox-list theme — desktop uses the WeChat-
    // style tinted gray that unpinned rows blend into; mobile is plain
    // white. Either way, only pinned / selected rows stand out.
    <View style={{ flex: 1, backgroundColor: theme.panel }}>
      <FlatList
        data={sortedThreads}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        contentContainerStyle={{ flexGrow: 1 }}
        ListEmptyComponent={
          loading ? (
            <ThreadListSkeleton />
          ) : (
            <ListEmptyState
              title="暂无消息"
              subtitle="点击右上角通讯录发起聊天"
              icon={<EmptyMessagesIcon />}
            />
          )
        }
      />
    </View>
  );
});
